import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/AuthContext';
import { UIProvider } from './store/UIContext';
import { ProtectedRoute, RoleRoute } from './routes/index';
import AppLayout from './layouts/AppLayout';
import AuthLayout from './layouts/AuthLayout';
import { isWorkspaceRole } from './utils/roleHelpers';
import MultiTabWorkspace from './components/workspace/MultiTabWorkspace';

// Marketing
import LandingPage from './pages/marketing/LandingPage';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import { RegisterPhonePage, ForgetPasswordPage, ResetPasswordPage } from './pages/auth/RegisterPhonePage';

// Dashboard
import RedirectDashboard from './pages/dashboard/RedirectDashboard';
import UserDashboard from './pages/dashboard/UserDashboard';

// Tickets
import NewTicketPage from './pages/tickets/NewTicketPage';
import ReferredOutTicketsPage from './pages/tickets/ReferredOutTicketsPage';
import TicketDetailPage from './pages/tickets/TicketDetailPage';
import TicketProcessPage from './pages/tickets/TicketProcessPage';
import TicketListPage from './pages/tickets/TicketListPage';
import { DispatcherPage, TicketDeptSelectionPage, AssignedUnansweredTicketsPage, TicketReassignPage, AiBulkRoutingPage } from './pages/tickets/DispatcherPage';
import DispatcherDepartmentDashboard from './pages/tickets/DispatcherDepartmentDashboard';
import DailyReportRoutingPage from './pages/dispatcher/DailyReportRoutingPage';
import InvoiceListPage from './pages/reports/InvoiceListPage';

// Users
import UserListPage from './pages/users/UserListPage';
import EmployeeListPage from './pages/users/EmployeeListPage';
import AddUserPage from './pages/users/AddUserPage';
import { ProfilePage, EditMyInfoPage } from './pages/users/ProfilePage';

// Companies
import CompanyListPage from './pages/companies/CompanyListPage';
import CompanyDetailPage from './pages/companies/CompanyDetailPage';
import AddCompanyPage from './pages/companies/AddCompanyPage';
import AddDepartmentPage from './pages/companies/AddDepartmentPage';
import DepartmentListPage from './pages/companies/DepartmentListPage';

// Reports
import DailyReportPage from './pages/reports/DailyReportPage';
import ManagerDailyReportPage from './pages/reports/ManagerDailyReportPage';
import DistanceReportPage from './pages/reports/DistanceReportPage';
import ActivationHistoryPage from './pages/reports/ActivationHistoryPage';
import PresenceReportPage from './pages/reports/PresenceReportPage';

// Feedback
import FeedbackPage from './pages/feedback/FeedbackPage';
import MyFeedbackInfoPage from './pages/feedback/MyFeedbackInfoPage';

// AI
import AIResponseDashboard from './pages/ai/AIResponseDashboard';
import AIInsightsDashboard from './pages/ai/AIInsightsDashboard';
import ManagerAIDashboard from './pages/admin/ManagerAIDashboard';
import AIStatisticsPage from './pages/admin/AIStatisticsPage';
import TrainingDatasetPage from './pages/admin/TrainingDatasetPage';
import SmartHelpPage from './pages/help/SmartHelpPage';

// Map
import CurrentMapPage from './pages/map/CurrentMapPage';
import AddPlacePage from './pages/map/AddPlacePage';
import PlacesListPage from './pages/map/PlacesListPage';
import MapTestPage from './pages/map/MapTestPage';

// Settings
import SettingsPage from './pages/settings/SettingsPage';
import SignaturePage from './pages/settings/SignaturePage';
import MenuOrderPage from './pages/settings/MenuOrderPage';
import DailyReportItemsPage from './pages/settings/DailyReportItemsPage';
import AutoReferralRulesPage from './pages/settings/AutoReferralRulesPage';
import CrossReferralSettingsPage from './pages/settings/CrossReferralSettingsPage';
import VoipTrunksPage from './pages/settings/VoipTrunksPage';
import GpsKnownPlacesPage from './pages/settings/GpsKnownPlacesPage';
import GpsDailyReportPage from './pages/reports/GpsDailyReportPage';
import EmployeeResponseRatingsPage from './pages/reports/EmployeeResponseRatingsPage';
import ManagerCompanyPermissionsPage from './pages/settings/ManagerCompanyPermissionsPage';
import AccessControlPage from './pages/settings/AccessControlPage';
import UsernamePrefixSettingsPage from './pages/settings/UsernamePrefixSettingsPage';
import ErrorAlertSettingsPage from './pages/settings/ErrorAlertSettingsPage';
import AIServicesMonitorPage from './pages/settings/AIServicesMonitorPage';
import SmsCampaignPage from './pages/sms/SmsCampaignPage';
import ManageCompanyDelegationPage from './pages/settings/ManageCompanyDelegationPage';

// Missions
import MissionDefinitionPage from './pages/missions/MissionDefinitionPage';
import MissionResponsePage from './pages/missions/MissionResponsePage';
import MyMissionsPage from './pages/missions/MyMissionsPage';

import './styles/tokens.css';
import './styles/global.css';

// /dashboard shows the multi-tab workspace for simple_employee/
// manager_employee only (see isWorkspaceRole) - every other role
// (dispatcher, manager_user, simple_user, guest_user) keeps the exact same
// single-page UserDashboard it always had.
function DashboardEntry() {
  const { user } = useAuth();
  return isWorkspaceRole(user?.type) ? <MultiTabWorkspace /> : <UserDashboard />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UIProvider>
          <Routes>
            {/* Marketing landing page kept reachable, but no longer the site root */}
            <Route path="/landing" element={<LandingPage />} />

            {/* Auth (no sidebar) — login is the site root */}
            <Route element={<AuthLayout />}>
              <Route path="/" element={<LoginPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/sign_up" element={<SignupPage />} />
              <Route path="/register_phone" element={<RegisterPhonePage />} />
              <Route path="/forget_password" element={<ForgetPasswordPage />} />
              <Route path="/reset_password" element={<ResetPasswordPage />} />
            </Route>

            {/* App (with sidebar) */}
            <Route element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }>
              {/* Dashboard */}
              <Route path="/home" element={<RedirectDashboard />} />
              <Route path="/dashboard" element={<DashboardEntry />} />

              {/* Tickets */}
              {/* Composing/sending a new ticket is a customer action only -
                  employees/dispatcher only ever respond to tickets already
                  assigned to them (see UserDashboard's "+ تیکت جدید" gate). */}
              <Route path="/tickets/new" element={
                <RoleRoute allowed={['simple_user', 'manager_user', 'guest_user']}>
                  <NewTicketPage />
                </RoleRoute>
              } />
              <Route path="/help" element={
                <RoleRoute allowed={['simple_user', 'manager_user', 'guest_user']}>
                  <SmartHelpPage />
                </RoleRoute>
              } />
              <Route path="/tickets/responded" element={<TicketListPage mode="responded" />} />
              <Route path="/tickets" element={<TicketListPage />} />
              <Route path="/tickets/:id" element={<TicketDetailPage />} />
              <Route path="/tickets/:id/process" element={<TicketProcessPage />} />
              <Route path="/tickets/:id/assign" element={<TicketDeptSelectionPage />} />
              <Route path="/tickets/referred" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <ReferredOutTicketsPage />
                </RoleRoute>
              } />

              {/* Dispatcher */}
              <Route path="/dispatcher" element={
                <RoleRoute allowed={['dispatcher', 'admin']}>
                  <DispatcherPage />
                </RoleRoute>
              } />
              <Route path="/dispatcher/assigned" element={
                <RoleRoute allowed={['dispatcher', 'admin']}>
                  <AssignedUnansweredTicketsPage />
                </RoleRoute>
              } />
              <Route path="/dispatcher/ai-routing" element={
                <RoleRoute allowed={['dispatcher', 'admin']}>
                  <AiBulkRoutingPage />
                </RoleRoute>
              } />
              <Route path="/tickets/:id/reassign" element={
                <RoleRoute allowed={['dispatcher', 'admin']}>
                  <TicketReassignPage />
                </RoleRoute>
              } />
              <Route path="/dispatcher/departments" element={
                <RoleRoute allowed={['dispatcher','manager_employee', 'admin']}>
                  <DispatcherDepartmentDashboard />
                </RoleRoute>
              } />
              <Route path="/dispatcher/daily-report-routing" element={
                <RoleRoute allowed={['dispatcher', 'admin']}>
                  <DailyReportRoutingPage />
                </RoleRoute>
              } />

              {/* Users */}
              <Route path="/users" element={<UserListPage />} />
              <Route path="/employees" element={
                <RoleRoute allowed={['dispatcher', 'manager_employee', 'admin']}>
                  <EmployeeListPage />
                </RoleRoute>
              } />
              <Route path="/users/add/company" element={<AddUserPage />} />
              <Route path="/users/add/employee" element={<AddUserPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/edit" element={<EditMyInfoPage />} />

              {/* Companies & Departments */}
              <Route path="/companies" element={<CompanyListPage />} />
              <Route path="/companies/add" element={<AddCompanyPage />} />
              <Route path="/companies/:id" element={<CompanyDetailPage />} />
              <Route path="/departments" element={<DepartmentListPage />} />
              <Route path="/departments/add" element={<AddDepartmentPage />} />

              {/* Reports */}
              <Route path="/reports/daily" element={<DailyReportPage />} />
              <Route path="/reports/invoices" element={
                <RoleRoute allowed={['simple_employee', 'manager_employee', 'dispatcher', 'admin']}>
                  <InvoiceListPage />
                </RoleRoute>
              } />
              <Route path="/reports/manager" element={<ManagerDailyReportPage />} />
              <Route path="/reports/distance" element={<DistanceReportPage />} />
              <Route path="/reports/presence" element={<PresenceReportPage />} />
              <Route path="/activation-history" element={
                <RoleRoute allowed={['dispatcher','manager_employee', 'admin']}>
                  <ActivationHistoryPage />
                </RoleRoute>
              } />

              {/* Feedback — manager_employee only; simple_employee has no feedback menu */}
              <Route path="/feedback" element={
                <RoleRoute allowed={['manager_employee', 'dispatcher', 'admin']}>
                  <FeedbackPage />
                </RoleRoute>
              } />
              <Route path="/feedback/mine" element={
                <RoleRoute allowed={['manager_employee', 'dispatcher', 'admin']}>
                  <MyFeedbackInfoPage />
                </RoleRoute>
              } />
              {/* Was EmployeeFeedbackListPage (feedbacks.feedback_grade) - that
                  table has zero rows, nothing ever writes to it, so this link
                  permanently showed an empty state. The real, populated
                  employee-feedback feature is the 1-5 star response_ratings
                  report (EmployeeResponseRatingsPage) - point the sidebar's
                  "بازخورد" link straight at it instead of a second, dead copy. */}
              <Route path="/feedback/team" element={
                <RoleRoute allowed={['manager_employee', 'dispatcher', 'admin']}>
                  <EmployeeResponseRatingsPage />
                </RoleRoute>
              } />

              {/* AI */}
              <Route path="/ai" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <AIResponseDashboard />
                </RoleRoute>
              } />
              <Route path="/ai/review" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <AIResponseDashboard />
                </RoleRoute>
              } />
              <Route path="/ai/insights" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <AIInsightsDashboard />
                </RoleRoute>
              } />
              <Route path="/ai/manager" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <ManagerAIDashboard />
                </RoleRoute>
              } />
              <Route path="/ai/statistics" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <AIStatisticsPage />
                </RoleRoute>
              } />
              <Route path="/ai/dataset" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <TrainingDatasetPage />
                </RoleRoute>
              } />

              {/* Map */}
              <Route path="/map" element={<CurrentMapPage />} />
              <Route path="/map/add-place" element={<AddPlacePage />} />
              <Route path="/map/places" element={
                <RoleRoute allowed={['dispatcher','manager_employee', 'admin']}>
                  <PlacesListPage />
                </RoleRoute>
              } />
              <Route path="/map/test" element={<MapTestPage />} />

              {/* Settings */}
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/menu" element={<MenuOrderPage />} />
              <Route path="/settings/signature" element={<SignaturePage />} />
              <Route path="/settings/daily-report-items" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <DailyReportItemsPage />
                </RoleRoute>
              } />
              <Route path="/settings/auto-referral" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <AutoReferralRulesPage />
                </RoleRoute>
              } />
              <Route path="/settings/cross-referral" element={
                <RoleRoute allowed={['admin']}>
                  <CrossReferralSettingsPage />
                </RoleRoute>
              } />
              <Route path="/settings/voip-trunks" element={
                <RoleRoute allowed={['admin']}>
                  <VoipTrunksPage />
                </RoleRoute>
              } />
              <Route path="/settings/gps-known-places" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <GpsKnownPlacesPage />
                </RoleRoute>
              } />
              <Route path="/reports/gps-daily-report" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <GpsDailyReportPage />
                </RoleRoute>
              } />
              <Route path="/reports/employee-response-ratings" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <EmployeeResponseRatingsPage />
                </RoleRoute>
              } />
              <Route path="/settings/manager-company-permissions" element={
                <RoleRoute allowed={['admin', 'dispatcher']}>
                  <ManagerCompanyPermissionsPage />
                </RoleRoute>
              } />
              <Route path="/settings/company-delegation" element={
                <RoleRoute allowed={['manager_employee', 'admin']}>
                  <ManageCompanyDelegationPage />
                </RoleRoute>
              } />
              <Route path="/settings/access-control" element={
                <RoleRoute allowed={['admin']}>
                  <AccessControlPage />
                </RoleRoute>
              } />
              <Route path="/settings/username-prefix" element={
                <RoleRoute allowed={['admin', 'dispatcher']}>
                  <UsernamePrefixSettingsPage />
                </RoleRoute>
              } />
              <Route path="/settings/error-alerts" element={
                <RoleRoute allowed={['admin']}>
                  <ErrorAlertSettingsPage />
                </RoleRoute>
              } />
              <Route path="/settings/ai-services" element={
                <RoleRoute allowed={['dispatcher', 'admin']}>
                  <AIServicesMonitorPage />
                </RoleRoute>
              } />

              <Route path="/sms-campaigns" element={
                <RoleRoute allowed={['dispatcher', 'admin']}>
                  <SmsCampaignPage />
                </RoleRoute>
              } />

              {/* Missions */}
              <Route path="/missions/define" element={<MissionDefinitionPage />} />
              <Route path="/missions/:id/respond" element={<MissionResponsePage />} />
              <Route path="/missions/my" element={<MyMissionsPage />} />

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Route>
          </Routes>
        </UIProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
